/**
 * A transcript search's state (TMMAPP-0148 U2 items 12–14; TMMAPI-0184 U2): the field's text, the
 * query it commits once the person pauses, and the hits. Shared by the search in a session and the
 * search across sessions.
 *
 * - **The query rule is the field's:** 2 to 100 characters once trimmed, counted as the server
 *   counts them (code points). Outside it nothing is sent and the field says the rule; a `400` the
 *   server still answers shows at the field too.
 * - **Debounced:** a query is sent once typing pauses (`SEARCH_DEBOUNCE_MS`); a later query's
 *   answer replaces an earlier one's, and an earlier one landing late is dropped.
 * - **More** (`more`) reads the next older page, appended in order, newest first. The page's
 *   cursor is `cursorOf`'s: a session's own search pages by the last hit's id (the default); the
 *   search across sessions by the page's served `nextBeforeId` (TMMAPI-0185 U1), an opaque string
 *   passed back as served, never built or parsed. With older hits but no cursor, More isn't offered.
 * - Nothing is stored in the browser.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AssistantSearchHit, AssistantSearchPage } from '@/api/assistant';

export const SEARCH_MIN_CHARS = 2;
export const SEARCH_MAX_CHARS = 100;
export const SEARCH_DEBOUNCE_MS = 300;

/** The query's length as the server counts it: code points, once trimmed. */
export function queryLength(text: string): number {
  return [...text.trim()].length;
}

export function validQuery(text: string): boolean {
  const length = queryLength(text);
  return length >= SEARCH_MIN_CHARS && length <= SEARCH_MAX_CHARS;
}

/** The next page's `beforeId`: a hit's id, or an opaque served cursor. */
export type SearchCursor = number | string;
export type SearchRun<C extends SearchCursor> = (
  q: string,
  beforeId?: C,
) => Promise<AssistantSearchPage>;
/** The cursor for the page after this one; null when there is none to send. */
export type CursorOf<C extends SearchCursor> = (page: AssistantSearchPage) => C | null;

/** A session's own search: the last hit's id. */
export const lastHitId: CursorOf<number> = (page) => page.items.at(-1)?.id ?? null;

export interface TranscriptSearch {
  /** What the field shows. */
  text: string;
  change: (text: string) => void;
  clear: () => void;
  /** The query sent (trimmed); '' while none is. */
  q: string;
  /** Something is typed that the rule refuses: the field says the rule. */
  outsideRule: boolean;
  /** The hits for `q`; null while its first page is read (or no query). */
  hits: AssistantSearchHit[] | null;
  hasMore: boolean;
  /** Older hits remain and their cursor is in hand: More is offered. */
  canMore: boolean;
  error: unknown;
  loadingMore: boolean;
  more: () => void;
}

interface Results<C> {
  q: string;
  hits: AssistantSearchHit[];
  hasMore: boolean;
  next: C | null;
}

/** The cursor after a page, while older hits remain. */
function nextOf<C extends SearchCursor>(
  page: AssistantSearchPage,
  cursorOf: CursorOf<C>,
): C | null {
  return page.hasMore && page.items.length > 0 ? cursorOf(page) : null;
}

export function useTranscriptSearch<C extends SearchCursor>(
  run: SearchRun<C>,
  cursorOf: CursorOf<C>,
): TranscriptSearch {
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Results<C> | null>(null);
  const [failed, setFailed] = useState<{ q: string; error: unknown } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const timer = useRef<number | null>(null);
  const runRef = useRef(run);
  const cursorRef = useRef(cursorOf);
  useEffect(() => {
    runRef.current = run;
    cursorRef.current = cursorOf;
  });

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);
  useEffect(() => clearTimer, [clearTimer]);

  const change = useCallback(
    (next: string) => {
      setText(next);
      clearTimer();
      timer.current = window.setTimeout(() => {
        timer.current = null;
        setQ(validQuery(next) ? next.trim() : '');
      }, SEARCH_DEBOUNCE_MS);
    },
    [clearTimer],
  );

  const clear = useCallback(() => {
    clearTimer();
    setText('');
    setQ('');
  }, [clearTimer]);

  useEffect(() => {
    if (q === '') return undefined;
    let live = true;
    runRef.current(q).then(
      (page) => {
        if (!live) return;
        setResults({
          q,
          hits: page.items,
          hasMore: page.hasMore,
          next: nextOf(page, cursorRef.current),
        });
        setFailed(null);
      },
      (error: unknown) => {
        if (live) setFailed({ q, error });
      },
    );
    return () => {
      live = false;
    };
  }, [q]);

  const current = results !== null && results.q === q ? results : null;
  const error = failed !== null && failed.q === q ? failed.error : null;

  const more = useCallback(() => {
    const next = current?.next ?? null;
    if (current === null || next === null || loadingMore) return;
    const at = current.q;
    setLoadingMore(true);
    runRef.current(at, next).then(
      (page) => {
        setResults((prev) =>
          prev !== null && prev.q === at
            ? {
                q: at,
                hits: [...prev.hits, ...page.items],
                hasMore: page.hasMore,
                next: nextOf(page, cursorRef.current),
              }
            : prev,
        );
        setLoadingMore(false);
      },
      (moreError: unknown) => {
        setFailed({ q: at, error: moreError });
        setLoadingMore(false);
      },
    );
  }, [current, loadingMore]);

  return {
    text,
    change,
    clear,
    q,
    outsideRule: text.trim() !== '' && !validQuery(text),
    hits: q === '' ? null : (current?.hits ?? null),
    hasMore: current?.hasMore ?? false,
    canMore: current?.next != null,
    error,
    loadingMore,
    more,
  };
}
